import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';

import { getShortAreaUnit } from '../../../../../shared/features/areaUnit/helpers/functions/areaUnit';
import CalculatorTable from '../../../../components/CalculatorTable';
import rootStore from '../../../rootStore/RootStore';
import usePriceConfig from '../../../../../shared/features/priceConfig/hooks/usePriceConfig';
import { getCurrencyForAreaUnit } from '../../../../../shared/helpers/functions/currency';
import {
  formatNumber,
  isNumber,
} from '../../../../../shared/helpers/functions/number';
import AmplitudeAnalytics from '../../../../../shared/services/AmplitudeAnalytics';
import { MAX_AREA_AMOUNTS } from '../../../../../shared/helpers/constants/areaAmounts';

const calculateActionAmount = (
  area: string,
  actionCost: number,
  actionQuantity: string,
) =>
  (parseInt(area, 10) ?? 0) * actionCost * (parseInt(actionQuantity, 10) ?? 0);

const InitialCostsCalculatorTable = observer(() => {
  const { t } = useTranslation();
  const { config } = usePriceConfig(rootStore);
  const { uiStateStore, areaUnitStore } = rootStore;
  const {
    monthlyPlan,
    setMonthlyPlanArea: setArea,
    setMonthlyPlanDatasetImport: setDatasetImport,
    setMonthlyPlanYieldCleaningCalibration: setYieldCleaningCalibration,
    setMonthlyPlanYieldSyntheticMapCreation: setYieldSyntheticMapCreation,
    setMonthlyPlanZonesMapCreation: setZonesMapCreation,
    setMonthlyPlanEquationMapCreation: setEquationMapCreation,
    setMonthlyPlanSamplingPlanCreation: setSamplingPlanCreation,
    setMonthlyPlanExportZonesMap: setExportZonesMap,
    setMonthlyPlanSeats: setUserSetsAmount,
    setMonthlyPlanPlanetImagery: setPlanetImageryAmount,
  } = uiStateStore;
  const { areaUnit } = areaUnitStore;

  const currency = getCurrencyForAreaUnit(areaUnit);
  const shortAreaUnit = getShortAreaUnit(areaUnit);
  const area = parseInt(monthlyPlan.area, 10);
  const additionalSeats = parseInt(monthlyPlan.additionalSeats, 10) ?? 0;
  const planetImagery = parseInt(monthlyPlan.planetImagery, 10) ?? 0;

  const monthlyFee =
    config?.MonthlyPayAsYouGo.BaseMonthlyFee.prices[currency].price ?? 0;
  const datasetImportCost =
    config?.MonthlyPayAsYouGo.DatasetImport.prices[currency].price ?? 0;
  const zonesMapCreationCost =
    config?.MonthlyPayAsYouGo.ZonesVRAMapsCreation.prices[currency].price ?? 0;
  const equationMapsCreationCost =
    config?.MonthlyPayAsYouGo.EquationMapsCreation.prices[currency].price ?? 0;
  const fieldRegistrationCost =
    config?.MonthlyPayAsYouGo.FieldRegistration.prices[currency].price ?? 0;
  const yieldCleaningCalibrationCost =
    config?.MonthlyPayAsYouGo.YieldCleaningAndCalibration.prices[currency]
      .price ?? 0;
  const yieldSyntheticMapCreationCost =
    config?.MonthlyPayAsYouGo.YieldSyntheticMapCreation.prices[currency]
      .price ?? 0;
  const itemExportCost =
    config?.MonthlyPayAsYouGo.Export.prices[currency].price ?? 0;
  const userSeatCost =
    config?.MonthlyPayAsYouGo.AdditionalUserSeat.prices[currency].price ?? 0;
  const planetImageryCost =
    config?.MonthlyPayAsYouGo.PlanetImagery.prices[currency].price ?? 0;
  const samplingPlanCreationCost =
    config?.MonthlyPayAsYouGo.SamplingPlanCreation.prices[currency].price ?? 0;

  const maxVolume = MAX_AREA_AMOUNTS[areaUnit];
  const maxVolumeExceeded = area && maxVolume && area > maxVolume;

  const handleInputChange = (
    value: string,
    setter: (value: string) => void,
  ) => {
    const parsedValue = parseInt(value, 10);

    if (isNumber(parsedValue)) {
      setter(parsedValue.toString());
    } else {
      setter('');
    }
  };

  const handleInputBlur = (
    value: string,
    setter: (value: string) => void,
    analytics: Parameters<
      typeof AmplitudeAnalytics.trackMonthlyPricingCalculated
    >[0],
  ) => {
    const parsedValue = parseInt(value, 10);

    if (parsedValue && parsedValue >= 0) {
      setter(parsedValue.toString());
    } else {
      setter('0');
    }

    AmplitudeAnalytics.trackMonthlyPricingCalculated(analytics);
  };

  const rows = [
    {
      title: t('plan-select.pricing-example.rows.core-features'),
      quantity: 1,
      quantityUnit: '',
      price: monthlyFee,
      amount: monthlyFee,
    },
    {
      title: t('plan-select.pricing-example.rows.field-registration'),
      quantity: area ?? 0,
      quantityUnit: shortAreaUnit,
      price: maxVolumeExceeded ? null : fieldRegistrationCost,
      amount: (area ?? 0) * fieldRegistrationCost,
      onQuantityChange: (value: string) => handleInputChange(value, setArea),
      onQuantityInputBlur: () =>
        handleInputBlur(monthlyPlan.area, setArea, {
          area,
          areaUnit,
        }),
    },
    {
      title: t('plan-select.pricing-example.rows.dataset-import'),
      quantity: parseInt(monthlyPlan.datasetImport, 10) ?? 0,
      quantityUnit: t('common.datasets-per-area', {
        area: formatNumber(monthlyPlan.area),
        areaUnit: shortAreaUnit,
      }),
      price: datasetImportCost,
      amount: calculateActionAmount(
        monthlyPlan.area,
        datasetImportCost,
        monthlyPlan.datasetImport,
      ),
      onQuantityChange: (value: string) =>
        handleInputChange(value, setDatasetImport),
      onQuantityInputBlur: () =>
        handleInputBlur(monthlyPlan.datasetImport, setDatasetImport, {
          datasetImport: parseInt(monthlyPlan.datasetImport, 10),
        }),
    },
    {
      title: t(
        'plan-select.pricing-example.rows.yield-cleaning-and-calibration',
      ),
      quantity: parseInt(monthlyPlan.yieldCleaningCalibration, 10) ?? 0,
      quantityUnit: t('common.datasets-per-area', {
        area: formatNumber(monthlyPlan.area),
        areaUnit: shortAreaUnit,
      }),
      price: yieldCleaningCalibrationCost,
      amount: calculateActionAmount(
        monthlyPlan.area,
        yieldCleaningCalibrationCost,
        monthlyPlan.yieldCleaningCalibration,
      ),
      onQuantityChange: (value: string) =>
        handleInputChange(value, setYieldCleaningCalibration),
      onQuantityInputBlur: () =>
        handleInputBlur(
          monthlyPlan.yieldCleaningCalibration,
          setYieldCleaningCalibration,
          {
            yieldCleaningCalibration: parseInt(
              monthlyPlan.yieldCleaningCalibration,
              10,
            ),
          },
        ),
    },
    {
      title: t('plan-select.pricing-example.rows.yield-synthetic-map-creation'),
      quantity: parseInt(monthlyPlan.yieldSyntheticMapCreation, 10) ?? 0,
      quantityUnit: t('common.maps-per-area', {
        area: formatNumber(monthlyPlan.area),
        areaUnit: shortAreaUnit,
      }),
      price: yieldSyntheticMapCreationCost,
      amount: calculateActionAmount(
        monthlyPlan.area,
        yieldSyntheticMapCreationCost,
        monthlyPlan.yieldSyntheticMapCreation,
      ),
      onQuantityChange: (value: string) =>
        handleInputChange(value, setYieldSyntheticMapCreation),
      onQuantityInputBlur: () =>
        handleInputBlur(
          monthlyPlan.yieldSyntheticMapCreation,
          setYieldSyntheticMapCreation,
          {
            yieldSyntheticMapCreation: parseInt(
              monthlyPlan.yieldSyntheticMapCreation,
              10,
            ),
          },
        ),
    },
    {
      title: t('plan-select.pricing-example.rows.zone-map-creation'),
      quantity: parseInt(monthlyPlan.zonesMapCreation, 10) ?? 0,
      quantityUnit: t('common.maps-per-area', {
        area: formatNumber(monthlyPlan.area),
        areaUnit: shortAreaUnit,
      }),
      price: zonesMapCreationCost,
      amount: calculateActionAmount(
        monthlyPlan.area,
        zonesMapCreationCost,
        monthlyPlan.zonesMapCreation,
      ),
      onQuantityChange: (value: string) =>
        handleInputChange(value, setZonesMapCreation),
      onQuantityInputBlur: () =>
        handleInputBlur(monthlyPlan.zonesMapCreation, setZonesMapCreation, {
          zonesMapCreation: parseInt(monthlyPlan.zonesMapCreation, 10),
        }),
    },
    {
      title: t('plan-select.pricing-example.rows.equation-map-creation'),
      quantity: parseInt(monthlyPlan.equationMapCreation, 10) ?? 0,
      quantityUnit: t('common.maps-per-area', {
        area: formatNumber(monthlyPlan.area),
        areaUnit: shortAreaUnit,
      }),
      price: equationMapsCreationCost,
      amount: calculateActionAmount(
        monthlyPlan.area,
        equationMapsCreationCost,
        monthlyPlan.equationMapCreation,
      ),
      onQuantityChange: (value: string) =>
        handleInputChange(value, setEquationMapCreation),
      onQuantityInputBlur: () =>
        handleInputBlur(
          monthlyPlan.equationMapCreation,
          setEquationMapCreation,
          {
            equationMapCreation: parseInt(monthlyPlan.equationMapCreation, 10),
          },
        ),
    },
    {
      title: t('plan-select.pricing-example.rows.sampling-plan-creation'),
      quantity: parseInt(monthlyPlan.samplingPlanCreation, 10) ?? 0,
      quantityUnit: t('common.plans-per-area', {
        area: formatNumber(monthlyPlan.area),
        areaUnit: shortAreaUnit,
      }),
      price: samplingPlanCreationCost,
      amount: calculateActionAmount(
        monthlyPlan.area,
        samplingPlanCreationCost,
        monthlyPlan.samplingPlanCreation,
      ),
      onQuantityChange: (value: string) =>
        handleInputChange(value, setSamplingPlanCreation),
      onQuantityInputBlur: () =>
        handleInputBlur(
          monthlyPlan.samplingPlanCreation,
          setSamplingPlanCreation,
          {
            samplingPlanCreation: parseInt(
              monthlyPlan.samplingPlanCreation,
              10,
            ),
          },
        ),
    },
    {
      title: t('plan-select.pricing-example.rows.export-zone-map'),
      quantity: parseInt(monthlyPlan.exportZonesMap, 10) ?? 0,
      quantityUnit: t('common.maps-per-area', {
        area: formatNumber(monthlyPlan.area),
        areaUnit: shortAreaUnit,
      }),
      price: itemExportCost,
      amount: calculateActionAmount(
        monthlyPlan.area,
        itemExportCost,
        monthlyPlan.exportZonesMap,
      ),
      onQuantityChange: (value: string) =>
        handleInputChange(value, setExportZonesMap),
      onQuantityInputBlur: () =>
        handleInputBlur(monthlyPlan.exportZonesMap, setExportZonesMap, {
          exportZonesMap: parseInt(monthlyPlan.exportZonesMap, 10),
        }),
    },
    {
      title: t('subscription-table.user-seat'),
      quantity: additionalSeats,
      quantityUnit: '',
      price: userSeatCost,
      amount: userSeatCost * additionalSeats,
      onQuantityChange: (value: string) =>
        handleInputChange(value, setUserSetsAmount),
      onQuantityInputBlur: () =>
        handleInputBlur(monthlyPlan.additionalSeats, setUserSetsAmount, {
          userSeats: additionalSeats,
        }),
    },
    {
      title: t('subscription-table.planet-imagery'),
      quantity: planetImagery,
      quantityUnit: shortAreaUnit,
      price: planetImageryCost,
      amount: planetImageryCost * planetImagery,
      onQuantityChange: (value: string) =>
        handleInputChange(value, setPlanetImageryAmount),
      onQuantityInputBlur: () =>
        handleInputBlur(monthlyPlan.planetImagery, setPlanetImageryAmount, {
          planetImagery,
        }),
    },
  ];

  const totalAmount = maxVolumeExceeded
    ? null
    : rows.reduce((acc, row) => acc + (row.amount || 0), 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {t('plans.monthly.pricing-example.initial-month-costs')}
      </Typography>
      <CalculatorTable
        areaUnit={areaUnit}
        rows={rows}
        total={{
          title: t('plan-select.pricing-example.total'),
          amount: totalAmount,
        }}
      />
    </Box>
  );
});

export default InitialCostsCalculatorTable;
